import { Assessment } from "../types";

const APP_VERSION = "2.0.0";
const FILE_VERSION = "2.0";

/**
 * Export assessment data to JSON file
 */
export const exportAssessment = (assessment: Assessment): void => {
  const data: Assessment = {
    ...assessment,
    appVersion: APP_VERSION,
    fileVersion: FILE_VERSION,
    lastEdited: new Date().toISOString(),
  };

  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const filename = `${assessment.title.replace(/\s+/g, "_")}_${formatDate(new Date())}.json`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
};

/**
 * Import assessment data from JSON file
 */
export const importAssessment = (file: File): Promise<Assessment> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content) as Assessment;

        // Validate required fields
        if (!data.title || !data.stacks || !Array.isArray(data.stacks)) {
          throw new Error("Invalid assessment file format");
        }

        resolve(data);
      } catch (error) {
        reject(new Error("Failed to parse assessment file"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

/**
 * Format date for filename
 */
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Save to localStorage
 */
export const saveToLocalStorage = (assessment: Assessment): void => {
  try {
    const data = {
      ...assessment,
      lastEdited: new Date().toISOString(),
    };
    localStorage.setItem("profitstacks-assessment", JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to localStorage:", error);
  }
};

/**
 * Load from localStorage
 */
export const loadFromLocalStorage = (): Assessment | null => {
  try {
    const data = localStorage.getItem("profitstacks-assessment");
    if (data) {
      return JSON.parse(data) as Assessment;
    }
  } catch (error) {
    console.error("Failed to load from localStorage:", error);
  }
  return null;
};
